<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import LocalizedInput from '@/components/LocalizedInput.vue';
import ImageField from '@/components/ImageField.vue';
import LocalizedEditor from '@/components/base/LocalizedEditor.vue';
import BaseDateInput from '@/components/base/BaseDateInput.vue';
import BaseTimeInput from '@/components/base/BaseTimeInput.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';

import type { Locales, ProjectModuleEventItemConfigDto } from '@/services/types/api.types';
import { computed, toRef } from 'vue';
import { formSaveImageGuard } from '@/helpers/form.helper';
import { useEventListItemsStore } from '@/stores/eventListItems.store';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import type { FileInfo } from '@/services/types/file.types';
import { useIsFormDirty } from 'vee-validate';
import { useTheme } from '@/composables/useTheme';
import { ModuleTypeEnum } from '@/types/modules';

// CONST
const { t } = useI18n();
const eventListItemsStore = useEventListItemsStore();

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  editLocale: Locales;
  module: ProjectModuleEventItemConfigDto;
  themeKey: string;
}>();

const emit = defineEmits<{
  (e: 'saveImage', options: SavingOptions): void;
  (e: 'deleteImage'): void;
}>();

// COMPUTED
const file = computed<FileInfo | null>(() => (props.module.config?.image as FileInfo) ?? null);
const statusOptions = computed(() => eventListItemsStore.getStatuses());
const isDirty = useIsFormDirty();
const themeConfiguration = useTheme('project', toRef(props, 'themeKey'));
const defaultCropping = { aspectRatio: 2, size: { width: 400, height: 200 } };
const themeCropping = themeConfiguration.getCroppingInfo(ModuleTypeEnum.Event, 1);
const cropping = computed(() => {
  const value = themeCropping.value;
  if (!value?.size?.width || !value?.size?.height) return defaultCropping;
  return value;
});

// FUNCTIONS
const handleSaveImage = async (options: SavingOptions) => {
  emit('saveImage', options);
};

function removeImage() {
  emit('deleteImage');
}

const handleClearImage = async () => {
  formSaveImageGuard(isDirty.value, removeImage);
};
</script>
<template>
  <div class="module-event-item">
    <localized-input
      name="title"
      :label="t('global.label.title')"
      :editLocale="editLocale"
      outlined
      :language_enabled="props.languageEnabled"
    />
    <q-field :label="t('global.label.description')" stack-label borderless>
      <template v-slot:control>
        <localized-editor
          name="description"
          :label="t('global.label.content')"
          class="q-mt-sm full-width"
          :editLocale="props.editLocale"
          :language_enabled="languageEnabled"
        />
      </template>
    </q-field>
    <localized-input
      name="location"
      :label="$t('project-module.event.item.location')"
      :editLocale="editLocale"
      outlined
      :language_enabled="props.languageEnabled"
    />

    <q-separator class="q-my-md" />

    <p class="text-subtitle1 q-mt-md q-mb-sm">{{ t('global.label.date') }}</p>
    <div class="row q-col-gutter-md">
      <div class="col-12 col-md-6">
        <base-date-input name="begin_date" :label="t('project-module.event.item.date.begin_date')" />
      </div>
      <div class="col-12 col-md-6">
        <base-date-input name="end_date" :label="t('project-module.event.item.date.end_date')" />
      </div>
    </div>

    <p class="text-subtitle1 q-mt-md q-mb-sm">{{ t('global.label.time') }}</p>
    <div class="row q-col-gutter-md">
      <div class="col-12 col-md-6">
        <base-time-input name="start_time" :label="t('project-module.event.item.date.start_time')" />
      </div>
      <div class="col-12 col-md-6">
        <base-time-input name="end_time" :label="t('project-module.event.item.date.end_time')" />
      </div>
    </div>

    <q-separator class="q-my-md" />

    <image-field
      :label="t('form.question.image.label')"
      version="v2"
      :cropping="cropping"
      :file="file"
      @clear-image="handleClearImage"
      @save-image="handleSaveImage"
      :form-dirty="isDirty"
      :language-enabled="props.languageEnabled"
      alt-text-name="config.image.metadata.image.altText"
    />

    <localized-select
      name="status"
      :label="t('project-module.event.item.status')"
      :editLocale="editLocale"
      :options="statusOptions"
      emit-value
      map-options
      outlined
    />
  </div>
</template>
